import type { RouteObject } from 'react-router-dom';
import { ReleasedFeature } from '../../release';
import { RequirePermission } from '../auth/guards';
import { CustomerTicketsPage } from '../tickets/CustomerTicketsPage';
import { AddProductPage, ChangeAccountTypePage } from './AccountTypePages';
import { ActivityPage } from './ActivityPage';
import { ArchivedNotesPage } from './ArchivedNotesPage';
import { BillingPage, PurchaseHistoryPage } from './BillingPages';
import { CustomerLayout } from './CustomerLayout';
import { CustomerProfilePage } from './CustomerProfilePage';
import { CustomerSearchPage } from './CustomerSearchPage';
import { EquipmentPurchasePage } from './EquipmentPurchasePage';
import { TopupPage } from './LineChangePages';
import { ModifyPage } from './ModifyPage';
import { AutoPayPage } from './MoneyPages';
import { PlanPurchasePage } from './PlanPurchasePage';
import { UsagePage } from './MoreActionForms';
import { OrdersPage } from './OrdersPage';
import { RecentSearchesPage } from './RecentSearchesPage';
import { SavedCardsPage } from './SavedCardsPage';
import { SentNotificationsPage } from './SentNotificationsPage';
import { StatusPage } from './StatusPage';

const guard = (code: string, element: JSX.Element) => (
  <RequirePermission code={code}>{element}</RequirePermission>
);

/**
 * Customer Service (PC400) routes, mounted under the app shell. A customer can only be
 * reached with PC56 (search and profile); every page below a customer needs its own code too.
 */
export const customersRoutes: RouteObject[] = [
  { path: 'customers', element: guard('PC56', <CustomerSearchPage />) },
  { path: 'customers/recent', element: guard('PC295', <RecentSearchesPage />) },
  // The Customer Service menu entries: the same search, opening another page of the customer.
  // The page reached checks its own permission.
  { path: 'customers/find/:section', element: guard('PC56', <CustomerSearchPage />) },
  {
    path: 'customers/:id',
    element: guard('PC56', <CustomerLayout />),
    children: [
      { index: true, element: <CustomerProfilePage /> },
      {
        path: 'notes/archive',
        element: (
          <ReleasedFeature name="notes-mark-void" page>
            <ArchivedNotesPage />
          </ReleasedFeature>
        ),
      },
      { path: 'status', element: guard('PC95', <StatusPage />) },
      { path: 'modify', element: guard('PC401', <ModifyPage />) },
      { path: 'billing', element: guard('PC75', <BillingPage />) },
      { path: 'purchases', element: guard('PC58', <PurchaseHistoryPage />) },
      { path: 'orders', element: guard('PC76', <OrdersPage />) },
      { path: 'activity', element: guard('PC770', <ActivityPage />) },
      { path: 'tickets', element: guard('PC87', <CustomerTicketsPage />) },
      { path: 'equipment', element: guard('PC69', <EquipmentPurchasePage />) },
      { path: 'topup', element: guard('PC60', <TopupPage key="minutes" kind="minutes" />) },
      { path: 'data-topup', element: guard('PC64', <TopupPage key="data" kind="data" />) },
      { path: 'plan-change', element: guard('PC77', <PlanPurchasePage kind="Change" />) },
      { path: 'plan-renew', element: guard('PC519', <PlanPurchasePage kind="Renew" />) },
      {
        path: 'account-type',
        element: guard(
          'PC648',
          <ReleasedFeature name="change-account-type" page>
            <ChangeAccountTypePage />
          </ReleasedFeature>,
        ),
      },
      {
        path: 'add-product',
        element: guard(
          'PC945',
          <ReleasedFeature name="add-product" page>
            <AddProductPage />
          </ReleasedFeature>,
        ),
      },
      { path: 'autopay', element: guard('PC574', <AutoPayPage />) },
      { path: 'usage', element: guard('PC254', <UsagePage />) },
      { path: 'cards', element: guard('PC900', <SavedCardsPage />) },
      { path: 'notifications', element: guard('PC930', <SentNotificationsPage />) },
    ],
  },
];
